import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";
import { Plus, Trash2, CheckCircle2, Circle, Pencil, MapPin, Clock, RefreshCw, SkipForward, User, ChevronDown, ChevronRight, Layers } from "lucide-react";
import { toast } from "sonner";
import TaskEditModal from "./TaskEditModal";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";

const ROLES = ["FOUNDER","BDOM","PCWM","GEHM","SMCM"];
const ROLE_LABEL = {
  FOUNDER: "Founder", BDOM: "BDOM", PCWM: "PCWM", GEHM: "GEHM", SMCM: "SMCM"
};
const PRIORITY_PILL = { high: "pill-red", medium: "pill-amber", low: "pill-gray" };
const priorityPill = (p) => PRIORITY_PILL[p] || "pill-gray";
const DEFAULT_COUNTRIES = ["India", "Nepal", "Sri Lanka", "South Africa", "Kenya", "Thailand", "Vietnam", "Uganda", "Nigeria", "Bolivia"];

export default function TaskList({ user, compact = false, filterRole = null, title = "Tasks" }) {
  const [tasks, setTasks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [title2, setTitle] = useState("");
  const [assignee, setAssignee] = useState(user.effective_role);
  const [priority, setPriority] = useState("medium");
  const [recurring, setRecurring] = useState("none");
  const [destination, setDestination] = useState("");
  const [city, setCity] = useState("");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [staffPool, setStaffPool] = useState([]);       // users of the chosen role (all)
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES);
  const [editing, setEditing] = useState(null);
  const [skipTarget, setSkipTarget] = useState(null);   // { task } → reason modal
  const [skipReason, setSkipReason] = useState("");
  const [delTarget, setDelTarget] = useState(null);     // task pending delete confirmation

  const load = useCallback(async () => {
    const { data } = await api.get("/tasks");
    setTasks(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const canRetarget = ["FOUNDER","BDOM"].includes(user.effective_role);
  useEffect(() => {
    if (!canRetarget) return;
    api.get("/accommodations/countries").catch(() => ({ data: { countries: [] } })).then(r => {
      const list = r.data?.countries;
      if (Array.isArray(list) && list.length) setCountries(list);
    });
    // Pull the full staff roster ONCE so the picker filters client-side by
    // role + destination + city without a fresh API call on every keystroke.
    api.get("/users").then(r => {
      const staff = (r.data || []).filter(u => ROLES.includes(u.role));
      setStaffPool(staff);
    }).catch(() => setStaffPool([]));
  }, [canRetarget]);

  useEffect(() => {
    const onUpdate = () => load();
    window.addEventListener("aii:task_update", onUpdate);
    return () => window.removeEventListener("aii:task_update", onUpdate);
  }, [load]);

  const shown = filterRole ? tasks.filter(t => t.assignee_role === filterRole) : tasks;
  // For the "all team tasks" view (filterRole == null), collapse the long
  // flat list into Country → City → Employee groups so it's actually
  // scannable. Users can also switch back to the flat list if they
  // prefer the old behaviour.
  const [grouped, setGrouped] = useState(!filterRole);
  const [collapsed, setCollapsed] = useState({});   // key -> bool
  const toggleCollapse = (k) => setCollapsed(prev => ({ ...prev, [k]: !prev[k] }));

  const groupedTasks = useMemo(() => {
    // {country: {city: {assignee: tasks[]}}}
    const staffById = Object.fromEntries(staffPool.map(u => [u.id, u]));
    const tree = {};
    for (const t of shown) {
      const country = (t.destination || t.country || "Unassigned").trim() || "Unassigned";
      const city = (t.city || "—").trim() || "—";
      const assignee = t.assignee_user_id
        ? (staffById[t.assignee_user_id]?.name || "Assigned user")
        : `${ROLE_LABEL[t.assignee_role] || t.assignee_role} (role)`;
      if (!tree[country]) tree[country] = {};
      if (!tree[country][city]) tree[country][city] = {};
      if (!tree[country][city][assignee]) tree[country][city][assignee] = [];
      tree[country][city][assignee].push(t);
    }
    return tree;
  }, [shown, staffPool]);

  const canAssignTo = (targetRole) => {
    if (user.effective_role === "FOUNDER") return true;
    if (user.effective_role === "BDOM") return ["BDOM","PCWM","GEHM","SMCM"].includes(targetRole);
    return targetRole === user.effective_role;
  };
  const assignableRoles = ROLES.filter(canAssignTo);

  // Narrow the staff picker to (role == assignee) AND destination/city if set.
  // Founder needs this so the same "Book taxi" task doesn't spam every PCWM
  // in India + Nepal.
  const staffMatches = staffPool.filter(u => {
    if (u.role !== assignee) return false;
    if (destination && (u.destination || "").toLowerCase() !== destination.toLowerCase()) return false;
    if (city && (u.city || "").toLowerCase() !== city.toLowerCase()) return false;
    return true;
  });

  // Whenever the role / office filters change, drop a stale user pick so the
  // Founder doesn't accidentally send a Jaipur PCWM task to a Kathmandu user.
  useEffect(() => {
    if (assigneeUserId && !staffMatches.some(s => s.id === assigneeUserId)) {
      setAssigneeUserId("");
    }
  }, [assignee, destination, city, staffMatches, assigneeUserId]);

  const add = async () => {
    if (!title2.trim()) { toast.error("Task needs a title"); return; }
    // Founder/BDOM sanity check — if you're assigning to a downstream role,
    // pick an office so it doesn't land on every PCWM/GEHM globally.
    if (canRetarget && assignee !== user.effective_role && !destination && !city && !assigneeUserId) {
      toast.error("Pick a destination + city (or a specific staff member) so the task lands in one office only");
      return;
    }
    try {
      const payload = { title: title2, assignee_role: assignee, priority, recurring };
      if (canRetarget && (destination || city)) {
        payload.destination = destination;
        payload.city = city;
      }
      if (assigneeUserId) payload.assignee_user_id = assigneeUserId;
      await api.post("/tasks", payload);
      setTitle(""); setDestination(""); setCity(""); setRecurring("none");
      setAssigneeUserId(""); setAdding(false);
      await load();
      toast.success("Task added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { done: !t.done });
    await load();
    if (!t.done) toast.success("Marked done ✓");
  };

  const del = async (task) => {
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted · restore from Recycle Bin");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setDelTarget(null);
  };

  const submitSkip = async () => {
    if (!skipTarget) return;
    try {
      const { data } = await api.post(`/tasks/${skipTarget.id}/skip`, { reason: skipReason.trim() });
      toast.success(`Skipped — next: ${data.new_due_date}`);
      setSkipTarget(null); setSkipReason("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Skip failed");
    }
  };

  const snooze = async (id) => {
    const iso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      await api.patch(`/tasks/${id}`, { snoozed_until: iso });
      await load();
      toast.success("Snoozed for 24 hours");
    } catch (e) { toast.error("Failed"); }
  };
  const unsnooze = async (id) => {
    try {
      await api.patch(`/tasks/${id}`, { snoozed_until: "" });
      await load();
      toast.success("Snooze cleared");
    } catch (e) { toast.error("Failed"); }
  };

  // Shared row renderer — used inline by both the flat list and the
  // grouped tree. Kept as a closure so it captures toggle/snooze/etc.
  const renderRow = (t) => (
    <div key={t.id} data-testid={`task-${t.id}`} className={`flex items-start gap-3 p-3 rounded-lg border ${t.done ? "border-[color:var(--aii-border)] bg-[color:var(--aii-bg)]" : "border-[color:var(--aii-border)] bg-white"}`}>
      <button data-testid={`task-toggle-${t.id}`} onClick={() => toggle(t)} className="mt-0.5 shrink-0">
        {t.done ? <CheckCircle2 className="text-brand" size={20} /> : <Circle className="text-[color:var(--aii-muted)]" size={20} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${t.done ? "line-through text-[color:var(--aii-muted)]" : ""}`}>{t.title}</div>
        <div className="flex items-center gap-2 text-xs text-[color:var(--aii-muted)] mt-1 flex-wrap">
          <span className={`pill ${priorityPill(t.priority)}`}>{t.priority}</span>
          {t.recurring && t.recurring !== "none" && (
            <span className="pill pill-amber inline-flex items-center gap-1" data-testid={`task-recurring-${t.id}`}
                  title={`Auto-regenerates ${t.recurring === "daily" ? "every day" : "every week"} when ticked done`}>
              <RefreshCw size={9}/> {t.recurring}
            </span>
          )}
          <span className="pill pill-green">→ {ROLE_LABEL[t.assignee_role]}</span>
          {(t.destination || t.city) && (
            <span className="pill pill-amber flex items-center gap-1" data-testid={`task-loc-${t.id}`}>
              <MapPin size={9}/> {[t.city, t.destination].filter(Boolean).join(", ")}
            </span>
          )}
          {t.snoozed_until && new Date(t.snoozed_until) > new Date() && (
            <span className="pill pill-gray flex items-center gap-1" data-testid={`task-snoozed-${t.id}`} title={`Snoozed until ${new Date(t.snoozed_until).toLocaleString()}`}>
              <Clock size={9}/> Snoozed
            </span>
          )}
          <span data-testid={`task-creator-${t.id}`}>
            by {t.created_by_name}
            {t.created_by_office && (
              <span className="text-[color:var(--aii-brand)] font-semibold ml-0.5">
                · {[t.created_by_city, t.created_by_office].filter(Boolean).join(", ")}
              </span>
            )}
          </span>
          {t.completed_at && <span>· done {new Date(t.completed_at).toLocaleDateString()}</span>}
        </div>
      </div>
      {(user.effective_role === "FOUNDER" || user.effective_role === "BDOM" || t.created_by_role === user.effective_role || t.assignee_role === user.effective_role || t.assignee_user_id === user.id) && (
        <>
          {(!t.snoozed_until || new Date(t.snoozed_until) <= new Date()) ? (
            <button data-testid={`task-snooze-${t.id}`} onClick={() => snooze(t.id)}
                    title="Snooze 24h — hides from nag banner"
                    className="text-[color:var(--aii-muted)] hover:text-brand"><Clock size={14}/></button>
          ) : (
            <button data-testid={`task-unsnooze-${t.id}`} onClick={() => unsnooze(t.id)}
                    title="Wake up now — cancels snooze"
                    className="text-brand"><Clock size={14}/></button>
          )}
          {t.recurring && t.recurring !== "none" && !t.done && (
            <button data-testid={`task-skip-${t.id}`}
                    onClick={() => { setSkipTarget(t); setSkipReason(""); }}
                    title="Skip today — bumps to next occurrence without marking done"
                    className="text-[color:var(--aii-muted)] hover:text-brand"><SkipForward size={14}/></button>
          )}
          <button data-testid={`task-edit-${t.id}`} onClick={() => setEditing(t)} className="text-[color:var(--aii-muted)] hover:text-brand"><Pencil size={14}/></button>
          <button data-testid={`task-del-${t.id}`} onClick={() => setDelTarget(t)}
                  title="Delete this task (added by mistake?)"
                  className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>
        </>
      )}
    </div>
  );

  return (
    <div className="aii-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <div className="flex items-center gap-2">
          {!filterRole && shown.length > 0 && (
            <button data-testid="task-group-toggle" onClick={() => setGrouped(g => !g)}
              title={grouped ? "Show as flat list" : "Group by Country → City → Employee"}
              className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
              <Layers size={12}/> {grouped ? "Flat list" : "Group"}
            </button>
          )}
          {assignableRoles.length > 0 && (
            <button data-testid="task-add-toggle" onClick={() => setAdding(a => !a)}
              className="flex items-center gap-1 text-sm text-brand font-semibold hover:underline">
              <Plus size={14} /> New task
            </button>
          )}
        </div>
      </div>

      {adding && (
        <div className="mb-4 p-4 bg-[color:var(--aii-bg)] rounded-lg space-y-2">
          <input data-testid="task-title-input" value={title2} onChange={e => setTitle(e.target.value)}
            placeholder="What needs to be done?"
            className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          <div className="flex gap-2">
            <select data-testid="task-assignee" value={assignee} onChange={e => setAssignee(e.target.value)}
              className="flex-1 px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              {assignableRoles.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
            <select value={priority} onChange={e => setPriority(e.target.value)}
              className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
            <select value={recurring} onChange={e => setRecurring(e.target.value)}
              data-testid="task-recurring"
              title="One-time = fires once. Daily/Weekly auto-recreate on tick."
              className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              <option value="none">One-time</option>
              <option value="daily">Daily recurring</option>
              <option value="weekly">Weekly recurring</option>
            </select>
            <button data-testid="task-save" onClick={add} className="bg-brand text-white px-4 rounded-md text-sm font-semibold hover:bg-brand-dark">Add</button>
          </div>
          {canRetarget && (
            <>
              <div className="flex gap-2 pt-1">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold flex items-center gap-1"><MapPin size={9}/> Route to office {assignee !== user.effective_role && <span className="text-red-600">*</span>}</label>
                  <select data-testid="task-destination" value={destination} onChange={e => setDestination(e.target.value)}
                    className="mt-0.5 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                    <option value="">— Pick a destination —</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold">City {assignee !== user.effective_role && <span className="text-red-600">*</span>}</label>
                  <input data-testid="task-city" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Kathmandu"
                    className="mt-0.5 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
                </div>
              </div>
              <div className="pt-1">
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold flex items-center gap-1"><User size={9}/> Assign to specific person (optional but recommended)</label>
                <select data-testid="task-assignee-user" value={assigneeUserId} onChange={e => setAssigneeUserId(e.target.value)}
                  className="mt-0.5 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                  <option value="">— Anyone with role {ROLE_LABEL[assignee]} in that office —</option>
                  {staffMatches.map(s => (
                    <option key={s.id} value={s.id}>{s.name} · {ROLE_LABEL[s.role]} · {[s.city, s.destination].filter(Boolean).join(", ") || "no office"}</option>
                  ))}
                </select>
                {staffMatches.length === 0 && (destination || city) && (
                  <div className="text-[11px] text-amber-700 mt-1" data-testid="task-no-staff-match">
                    No {ROLE_LABEL[assignee]} found in {[city, destination].filter(Boolean).join(", ")}. Task will still be role-scoped to that office.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* --- inline row renderer used by both flat + grouped views --- */}
      {(() => null)()}
      <div className="space-y-2">
        {shown.length === 0 && <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">No tasks yet.</div>}
        {!grouped && shown.map(t => renderRow(t))}
        {grouped && shown.length > 0 && (
          <div className="space-y-2" data-testid="task-grouped-view">
            {Object.entries(groupedTasks).sort(([a],[b]) => a.localeCompare(b)).map(([country, cities]) => {
              const kCountry = `c/${country}`;
              const isCol = !!collapsed[kCountry];
              const countTotal = Object.values(cities).reduce((acc, byCity) => acc + Object.values(byCity).reduce((a, arr) => a + arr.length, 0), 0);
              const doneTotal = Object.values(cities).reduce((acc, byCity) => acc + Object.values(byCity).reduce((a, arr) => a + arr.filter(t => t.done).length, 0), 0);
              return (
                <div key={country} className="border border-[color:var(--aii-border)] rounded-lg overflow-hidden" data-testid={`group-country-${country}`}>
                  <button type="button" onClick={() => toggleCollapse(kCountry)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-[color:var(--aii-green-tint)] hover:bg-[color:var(--aii-green-tint)]/80 text-sm font-semibold">
                    <span className="flex items-center gap-2">
                      {isCol ? <ChevronRight size={14}/> : <ChevronDown size={14}/>}
                      <span>{country}</span>
                      <span className="text-xs text-[color:var(--aii-muted)] font-normal">· {countTotal} task{countTotal===1?"":"s"}{doneTotal ? ` · ${doneTotal} done` : ""}</span>
                    </span>
                  </button>
                  {!isCol && (
                    <div className="p-2 space-y-2">
                      {Object.entries(cities).sort(([a],[b]) => a.localeCompare(b)).map(([city, byAssignee]) => {
                        const kCity = `c/${country}/${city}`;
                        const isColC = !!collapsed[kCity];
                        const cityTotal = Object.values(byAssignee).reduce((a, arr) => a + arr.length, 0);
                        return (
                          <div key={city} className="border border-[color:var(--aii-border)] rounded" data-testid={`group-city-${country}-${city}`}>
                            <button type="button" onClick={() => toggleCollapse(kCity)}
                              className="w-full flex items-center justify-between px-3 py-1.5 bg-[color:var(--aii-bg)] hover:bg-[color:var(--aii-bg)]/70 text-xs font-semibold">
                              <span className="flex items-center gap-1.5">
                                {isColC ? <ChevronRight size={12}/> : <ChevronDown size={12}/>}
                                <MapPin size={11}/> {city}
                                <span className="text-[color:var(--aii-muted)] font-normal">· {cityTotal}</span>
                              </span>
                            </button>
                            {!isColC && (
                              <div className="p-2 space-y-2">
                                {Object.entries(byAssignee).sort(([a],[b]) => a.localeCompare(b)).map(([assignee, arr]) => {
                                  const kAssign = `c/${country}/${city}/${assignee}`;
                                  const isColA = !!collapsed[kAssign];
                                  return (
                                    <div key={assignee} className="border border-[color:var(--aii-border)] rounded" data-testid={`group-assignee-${assignee}`}>
                                      <button type="button" onClick={() => toggleCollapse(kAssign)}
                                        className="w-full flex items-center justify-between px-3 py-1 hover:bg-[color:var(--aii-bg)] text-xs">
                                        <span className="flex items-center gap-1.5">
                                          {isColA ? <ChevronRight size={11}/> : <ChevronDown size={11}/>}
                                          <User size={11}/>
                                          <span className="font-semibold">{assignee}</span>
                                          <span className="text-[color:var(--aii-muted)]">· {arr.length}</span>
                                        </span>
                                      </button>
                                      {!isColA && (
                                        <div className="p-2 space-y-2">
                                          {arr.map(t => renderRow(t))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {editing && (
        <TaskEditModal task={editing} user={user} onClose={()=>setEditing(null)} onSaved={load}/>
      )}
      {skipTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSkipTarget(null)} data-testid="skip-reason-modal">
          <div className="bg-white max-w-md w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Skip today</div>
                <h3 className="font-display font-semibold text-lg">{skipTarget.title}</h3>
                <div className="text-xs text-[color:var(--aii-muted)] mt-1">Bumps to next occurrence · a Founder can review skip patterns later.</div>
              </div>
              <button onClick={() => setSkipTarget(null)} className="text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text)]"><SkipForward size={16}/></button>
            </div>
            <div className="p-5">
              <label className="text-xs text-[color:var(--aii-muted)] mb-1 block">Why are you skipping today?</label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="skip-reason-chips">
                {["Leave", "Cook absent", "No tourist", "Festival", "Monsoon", "Illness", "Vendor issue", "Other"].map(chip => {
                  const active = skipReason === chip;
                  return (
                    <button
                      key={chip}
                      type="button"
                      data-testid={`skip-chip-${chip.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setSkipReason(active ? "" : chip)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition ${active ? "bg-brand text-white border-brand" : "border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}
                    >
                      {chip}
                    </button>
                  );
                })}
              </div>
              <textarea autoFocus data-testid="skip-reason-input" rows={2} value={skipReason} onChange={(e) => setSkipReason(e.target.value)}
                placeholder="Or type a custom reason…"
                className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
              <div className="text-[10px] text-[color:var(--aii-muted)] mt-1">Pick a chip for a one-tap answer or edit the text for extra detail.</div>
            </div>
            <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2">
              <button onClick={() => setSkipTarget(null)} className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)]">Cancel</button>
              <button data-testid="skip-reason-submit" onClick={submitSkip} className="text-sm px-3 py-1.5 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1.5">
                <SkipForward size={13}/> Skip today
              </button>
            </div>
          </div>
        </div>
      )}
      <DeleteConfirmDialog
        open={!!delTarget}
        title="Delete this task?"
        description="The task disappears from every dashboard immediately."
        label={delTarget?.title}
        onCancel={() => setDelTarget(null)}
        onConfirm={() => del(delTarget)}
      />
    </div>
  );
}

